import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';

import { DropdownItem } from 'shopper/components/Dropdown';
import Icon from 'shopper/components/Icon';

import useToast from 'hooks/useToast';

import { sendEvent } from 'lib/gtag';
import { getOfferPageEventCategory } from 'lib/offer';
import { post } from 'lib/request';

import { useModal } from 'providers/ModalProvider';

import OFFER from 'constants/offer';

const ReportOfferModal = dynamic(() => import('./ReportModal'), { ssr: false });

const OTHER_REPORT_TYPE = 'other';
const DROPDOWN_ITEMS = [
  {
    name: 'Esta oferta terminou',
    isReportForFinishedOffer: false,
    gaEventAction: 'report_offer_finished',
    reportType: 'offer is finished',
  },
  {
    name: 'O produto está caro',
    isReportForFinishedOffer: false,
    gaEventAction: 'report_offer_too_expensive',
    reportType: 'offer price too high',
  },
  {
    name: 'Valor diferente do anunciado',
    isReportForFinishedOffer: false,
    gaEventAction: 'report_offer_price',
    reportType: 'wrong offer price',
  },
  {
    name: 'Oferta já foi postada',
    isReportForFinishedOffer: false,
    gaEventAction: 'report_offer_duplicated',
    reportType: 'offer is duplicated',
  },
  {
    name: 'Categoria incorreta',
    isReportForFinishedOffer: false,
    gaEventAction: 'report_offer_wrong_category',
    reportType: 'wrong offer category',
  },
  {
    gaEventAction: 'report_offer_active',
    isReportForFinishedOffer: true,
    name: 'Esta oferta voltou',
    reportType: 'offer is active',
    toastText: 'Obrigado! Seu alerta foi recebido',
  },
  {
    name: 'Outro...',
    isReportForFinishedOffer: true,
    gaEventAction: 'report_other',
    reportType: OTHER_REPORT_TYPE,
  },
];

const postOfferReport = async (reportOpts) => {
  const { omit } = await import('lib/object');
  const { offerId } = reportOpts;
  const body = omit(reportOpts, ['offerId', 'toastText']);

  return post(`/offer/${offerId}/report`, { body });
};

const ReportDropdownOptions = ({
  offerId,
  offerStatusName,
  onCloseDropdown,
}) => {
  const { showToast } = useToast();
  const { showModal } = useModal();
  const [mutateOfferReport] = useMutation(postOfferReport, {
    // Optimistically assumes that the mutation is successful and
    // shows a success feedback, even when its not
    onMutate: ({ toastText = 'Oferta reportada' }) => {
      onCloseDropdown();
      showToast({
        iconLeft: <Icon name="info-sign" />,
        children: toastText,
        secondsToAutoClose: 4,
      });
    },
  });

  const mutateOfferReportWithOfferId = (reportOpts) =>
    mutateOfferReport({ offerId, ...reportOpts });

  return (
    <>
      {DROPDOWN_ITEMS.map(
        ({
          name,
          isReportForFinishedOffer,
          gaEventAction,
          reportType,
          toastText,
        }) => {
          if (
            offerStatusName !== OFFER.STATUSES.FINISHED &&
            isReportForFinishedOffer &&
            reportType !== OTHER_REPORT_TYPE
          ) {
            return null;
          }

          if (
            offerStatusName === OFFER.STATUSES.FINISHED &&
            !isReportForFinishedOffer
          ) {
            return null;
          }

          return (
            <DropdownItem
              key={name}
              onClick={() => {
                sendEvent({
                  action: gaEventAction,
                  category: getOfferPageEventCategory(offerStatusName),
                });

                if (reportType === OTHER_REPORT_TYPE) {
                  onCloseDropdown();
                  showModal(ReportOfferModal, {
                    offerId,
                    isOpen: true,
                  });
                  return;
                }

                mutateOfferReportWithOfferId({
                  reportType,
                  toastText,
                });
              }}
            >
              {name}
            </DropdownItem>
          );
        }
      )}
    </>
  );
};

ReportDropdownOptions.defaultProps = {
  onCloseDropdown: () => {},
};

ReportDropdownOptions.propTypes = {
  offerId: PropTypes.number.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  onCloseDropdown: PropTypes.func,
};

export default ReportDropdownOptions;
